import { gql } from "@apollo/client";

/* FRAGMENTS */

export const REALESTATE_DETAILS_ALL_INVOICES = gql`
    fragment realestate_details_all_invoices on common_realestates {
        id
        name
        number
        consolidation
        houses(limit: 1) {
            zip
            city
        }
    }
`;

export const UNIT_DETAILS = gql`
    fragment unit_details on common_units {
        id
        number
        name
        houseid
    }
`;

export const REALESTATE_DETAILS = gql`
    fragment invoice_realestate_details on common_realestates {
        id
        name
        number
        consolidation
        consolidationid
        houses(limit: 1) {
            zip
            city
        }
        thumbnail: v_realestatefiles(
            where: { type: { _eq: 311 } }
            order_by: { filedate: desc, fileid: desc }
            distinct_on: fileid
            limit: 1
        ) {
            fileid
        }
    }
`;

export const USER_DETAIL = gql`
    fragment invoiceuser_details on ums_users {
        id
        name1
        name2
    }
`;

export const PAYMENTACCOUNT_DETAILS = gql`
    fragment paymentaccount_details on cred_paymentaccounts {
        id
        iban
        name
        type
        valid
    }
`;

export const V_TICKET_DETAILS = gql`
    fragment v_ticket_details on cred_v_tickets {
        incidentid
        realestateid
        unitid
        date
        date_long
        number
        title
    }
`;

export const INVOICE_FILES_DETAILS = gql`
    fragment invoicefiles_details on file_v_invoicefiles {
        invoicefileid: id
        fileid
        type
    }
`;

export const V_ORDER_DETAILS = gql`
    ${V_TICKET_DETAILS}
    fragment v_order_details on cred_v_orders {
        orderid
        realestateid
        date
        date_long
        number
        title
        instruction
        contractorid
        name1
        name2
        contractortype
        ticket: incident {
            ...v_ticket_details
        }
    }
`;

export const ACCOUNT_DETAILS = gql`
    fragment account_details on cred_accounts {
        id
        name
        number
        validfrom
        validto
        requirescostcenter
        requiresquantitiy
        unitofmeasure
        extracostdate
        vatconfig
        defaultvatcodeid
    }
`;

export const VAT_DETAILS = gql`
    fragment vat_details on cred_v_vatcodes_vatrates {
        vatrate_resolved
        vatrate_language
        vatrate_id
        vatcodeshort
        vatcodename
        vatcode_id
        validto
        validfrom
        realestateid
        rate
        vatratetype
    }
`;

export const COSTCENTER_DETAILS = gql`
    fragment costcenter_details on cred_costcenters {
        id
        name
        short
    }
`;

export const INPUTWORKFLOW_FILE_DETAILS = gql`
    ${REALESTATE_DETAILS_ALL_INVOICES}
    fragment inputworkflow_file_details on file_inputworkflowfiles {
        id
        inputworkflowid
        parentfileid
        state
        realestateid
        realestate {
            ...realestate_details_all_invoices
        }
        file {
            id
            name
        }
    }
`;

export const INPUTWORKFLOW_FILE_CHILD_DETAILS = gql`
    ${INPUTWORKFLOW_FILE_DETAILS}
    fragment inputworkflow_file_child_details on file_inputworkflowfiles {
        ...inputworkflow_file_details
        children(where: { state: { _nin: [99] } }) {
            ...inputworkflow_file_details
            children(where: { state: { _nin: [99] } }) {
                ...inputworkflow_file_details
            }
        }
    }
`;

export const INPUTWORKFLOW_DETAILS = gql`
    ${INPUTWORKFLOW_FILE_CHILD_DETAILS}
    fragment inputworkflow_details on cred_inputworkflows {
        id
        origin
        state
        emailid
        workflowinstance
        inputworkflowfiles(where: { parentfileid: { _is_null: true }, state: { _nin: [99] } }) {
            ...inputworkflow_file_child_details
        }
    }
`;

export const V_CREDITOR_DETAILS = gql`
    ${PAYMENTACCOUNT_DETAILS}
    fragment v_creditor_details on cred_v_creditors {
        creditorid
        creditorname
        creditornumber
        personid
        email
        gender
        name1
        name2
        phonebusiness
        mobile
        person_origin
        zip
        city
        street
        housenumber
        paymentaccounts {
            ...paymentaccount_details
        }
    }
`;

export const ACCOUNTING_DETAILS = gql`
    ${ACCOUNT_DETAILS}
    ${COSTCENTER_DETAILS}
    ${REALESTATE_DETAILS}
    fragment accounting_details on cred_accountings {
        id
        amount
        date
        text
        quantity
        extracostdate
        vatcodeid
        vatrate
        netamount
        vatamount
        realestate {
            ...invoice_realestate_details
        }
        account {
            ...account_details
        }
        costcenter {
            ...costcenter_details
        }
    }
`;

/* HISTORY VIEW */
export const INVOICE_HISTORY_DETAIL = gql`
    fragment invoice_history_detail on cred_v_invoice_history {
        invoiceid
        type
        inserted
        history_username
        old_values
        new_values
        comment_username
        comment_text
        email_subject
        email_body
        email_type
        email_recipients
        email_sender
        entityid
        inboundfiles: v_inboundfiles {
            id: fileid
            name
        }
        failed_outbound_email_recipients: outboundemailrecipients(
            where: { emaillogs: { event: { _nin: ["delivered", "processed", "open"] } } }
        ) {
            id
            emailaddress
            emaillogs(order_by: { timestamp: desc }) {
                id
                event
                message
            }
        }
    }
`;

export const GET_HISTORY_OF_INVOICE = gql`
    ${INVOICE_HISTORY_DETAIL}
    query GetHistoryOfInvoice($invoiceId: uuid) {
        cred_v_invoice_history(where: { invoiceid: { _eq: $invoiceId } }, order_by: { inserted: desc }) {
            ...invoice_history_detail
        }
    }
`;

export const INVOICE_DETAILS_ALL_INVOICES = gql`
    ${REALESTATE_DETAILS_ALL_INVOICES}
    ${USER_DETAIL}
    fragment invoice_details_all_invoices on cred_invoices {
        id
        type
        number
        date
        duedate
        state
        invoicenumber
        amount
        creditor {
            id
            name
            number
            personid
        }
        realestate {
            ...realestate_details_all_invoices
        }
        insertuserinfo {
            ...invoiceuser_details
        }
        workflowusers {
            id
            role
            user {
                ...invoiceuser_details
            }
        }
    }
`;

export const INVOICE_DETAILS = gql`
    ${REALESTATE_DETAILS}
    ${UNIT_DETAILS}
    ${USER_DETAIL}
    ${V_CREDITOR_DETAILS}
    ${PAYMENTACCOUNT_DETAILS}
    ${V_TICKET_DETAILS}
    ${V_ORDER_DETAILS}
    ${INVOICE_FILES_DETAILS}
    ${ACCOUNTING_DETAILS}
    ${INVOICE_HISTORY_DETAIL}
    fragment invoice_details on cred_invoices {
        id
        type
        number
        date
        duedate
        paiddate
        state
        invoicenumber
        amount
        paymentaccountid
        payoutaccountid
        paymentinfo
        workflowinstance
        duplicates(path: "duplicates")
        paymentaccount {
            ...paymentaccount_details
        }
        creditor: v_creditor {
            ...v_creditor_details
        }
        ticket: v_incident {
            ...v_ticket_details
        }
        order: v_order {
            ...v_order_details
        }
        realestate {
            ...invoice_realestate_details
        }
        unit {
            ...unit_details
        }
        insertuserinfo {
            ...invoiceuser_details
        }
        workflowusers(order_by: { role: asc }) {
            id
            role
            signaturestamp
            user {
                ...invoiceuser_details
            }
        }
        invoicefiles: v_invoicefiles(where: { deleted: { _eq: false } }, order_by: { inserted: desc }) {
            ...invoicefiles_details
        }
        accountings(order_by: { sort: asc }) {
            ...accounting_details
        }
        history(order_by: { inserted: desc }) {
            ...invoice_history_detail
        }
    }
`;

/* QUERIES */

export const GET_ALL_INVOICES = gql`
    ${INVOICE_DETAILS_ALL_INVOICES}
    query GetAllInvoices($limit: Int = 99999, $offset: Int = 0) {
        cred_invoices(order_by: { number: desc }, limit: $limit, offset: $offset) {
            ...invoice_details_all_invoices
        }
    }
`;

export const GET_ALL_OPEN_INVOICES = gql`
    ${INVOICE_DETAILS_ALL_INVOICES}
    query GetAllOpenInvoices($limit: Int = 99999, $offset: Int = 0) {
        cred_invoices(
            order_by: { number: desc }
            limit: $limit
            offset: $offset
            where: { _or: [{ state: { _lt: 90 } }, { state: { _eq: 95 } }] }
        ) {
            ...invoice_details_all_invoices
        }
    }
`;

export const GET_INVOICE_BY_NUMBER = gql`
    ${INVOICE_DETAILS}
    query GetInvoiceByNumber($invoiceNumber: Int) {
        cred_invoices(where: { number: { _eq: $invoiceNumber } }, limit: 1) {
            ...invoice_details
        }
    }
`;

export const GET_INVOICE_NUMBER_BY_ID = gql`
    ${INVOICE_DETAILS}
    query GetInvoiceNumberById($id: uuid!) {
        cred_invoices(where: { id: { _eq: $id } }, limit: 1) {
            id
            number
        }
    }
`;

export const GET_CREDITOR_DATA = gql`
    ${V_CREDITOR_DETAILS}
    query GetCreditorData {
        creditors: cred_v_creditors {
            ...v_creditor_details
        }
    }
`;

export const GET_CREDITORS_WITH_QUERY = gql`
    ${V_CREDITOR_DETAILS}
    query GetCreditorsWithQuery($searchQuery: String) {
        creditors: cred_v_creditors(
            where: {
                _or: [
                    { creditorname: { _ilike: $searchQuery } }
                    { name1: { _ilike: $searchQuery } }
                    { name2: { _ilike: $searchQuery } }
                    { number: { _ilike: $searchQuery } }
                    { mobile: { _ilike: $searchQuery } }
                    { phonebusiness: { _ilike: $searchQuery } }
                    { email: { _ilike: $searchQuery } }
                    { city: { _ilike: $searchQuery } }
                    { zip: { _ilike: $searchQuery } }
                    { street: { _ilike: $searchQuery } }
                ]
            }
        ) {
            ...v_creditor_details
        }
    }
`;

export const GET_TICKET_DATA = gql`
    ${V_TICKET_DETAILS}
    query GetTicketData {
        tickets: cred_v_tickets(order_by: { number: desc }) {
            ...v_ticket_details
        }
    }
`;

export const GET_TICKET_DATA_BY_REALESTATEID = gql`
    ${V_TICKET_DETAILS}
    query GetTicketDataByRealestateId($realestateid: uuid) {
        tickets: cred_v_tickets(order_by: { number: desc }, where: { realestateid: { _eq: $realestateid } }) {
            ...v_ticket_details
        }
    }
`;

export const GET_TICKETS_WITH_QUERY = gql`
    ${V_TICKET_DETAILS}
    query GetTicketsWithQuery($searchQuery: String) {
        tickets: cred_v_tickets(
            where: {
                _or: [
                    { date: { _ilike: $searchQuery } }
                    { date_long: { _ilike: $searchQuery } }
                    { number: { _ilike: $searchQuery } }
                    { title: { _ilike: $searchQuery } }
                ]
            }
        ) {
            ...v_ticket_details
        }
    }
`;

export const GET_ORDER_DATA = gql`
    ${V_ORDER_DETAILS}
    query GetOrderData {
        orders: cred_v_orders(order_by: { number: desc }) {
            ...v_order_details
        }
    }
`;

export const GET_ORDER_DATA_BY_REALESTATEID = gql`
    ${V_ORDER_DETAILS}
    query GetOrderDataByRealestateId($realestateid: uuid) {
        orders: cred_v_orders(order_by: { number: desc }, where: { realestateid: { _eq: $realestateid } }) {
            ...v_order_details
        }
    }
`;

export const GET_ORDER_DATA_BY_INCIDENT_ID = gql`
    ${V_ORDER_DETAILS}
    query GetOrderDataByIncidentId($incidentid: uuid) {
        orders: cred_v_orders(order_by: { number: desc }, where: { incidentid: { _eq: $incidentid } }) {
            ...v_order_details
        }
    }
`;

export const GET_ORDER_DATA_BY_CREDITOR_ID = gql`
    ${V_ORDER_DETAILS}
    query GetOrderDataByCreditorId($creditorid: uuid) {
        orders: cred_v_orders(order_by: { number: desc }, where: { contractorid: { _eq: $creditorid } }) {
            ...v_order_details
        }
    }
`;

export const GET_ORDERS_WITH_QUERY = gql`
    ${V_ORDER_DETAILS}
    query GetOrdersWithQuery($searchQuery: String) {
        orders: cred_v_orders(
            where: {
                _or: [
                    { date: { _ilike: $searchQuery } }
                    { date_long: { _ilike: $searchQuery } }
                    { number: { _ilike: $searchQuery } }
                    { instruction: { _ilike: $searchQuery } }
                    { name1: { _ilike: $searchQuery } }
                ]
            }
        ) {
            ...v_order_details
        }
    }
`;

export const GET_ACTIVE_MANAGER_USERS = gql`
    query GetActiveManagerUsers {
        ums_v_customer_users(
            where: {
                activated: { _eq: true }
                system: { _eq: false }
                blocked: { _eq: false }
                roles: { role: { _eq: 30 } }
            }
        ) {
            userid
            name
        }
    }
`;

export const GET_ACCOUNT_DATA = gql`
    ${ACCOUNT_DETAILS}
    query GetAccountData($realestateid: uuid, $bookingdate: date) {
        accounts: cred_accounts(
            where: {
                realestateid: { _eq: $realestateid }
                validfrom: { _lte: $bookingdate }
                _or: [{ validto: { _gte: $bookingdate } }, { validto: { _is_null: true } }]
            }
            order_by: { type: asc, number: asc, name: asc }
        ) {
            ...account_details
        }
    }
`;

export const GET_VAT_DATA = gql`
    ${VAT_DETAILS}
    query GetVatData($language: String = "de") {
        vatdata: cred_v_vatcodes_vatrates(where: { vatrate_language: { _eq: $language } }, order_by: { rate: asc }) {
            ...vat_details
        }
    }
`;

export const GET_COSTCENTER_DATA = gql`
    ${COSTCENTER_DETAILS}
    query GetCostCenterData($accountid: uuid) {
        accountcostcenters: cred_accountcostcenters(
            where: { accountid: { _eq: $accountid } }
            order_by: { costcenter: { short: asc, name: asc } }
        ) {
            costcenter {
                ...costcenter_details
            }
        }
    }
`;

export const GET_PAYOUTACCOUNT_DATA = gql`
    query GetPayoutAccountData($realestateid: uuid!) {
        payoutaccounts: cred_payoutaccounts(order_by: { name: asc }, where: { realestateid: { _eq: $realestateid } }) {
            id
            name
            iban
            valid
        }
    }
`;

export const GET_ACCOUNT_TAG_DATA = gql`
    query GetAccountTagData($creditorId: uuid, $realestateId: uuid, $bookingDate: date) {
        accounts_history: cred_get_filtered_accounts_history(
            args: { creditor_id_param: $creditorId, realestate_id_param: $realestateId, date_param: $bookingDate }
            order_by: { number_of_records: desc }
        ) {
            account_id
            account_name
            accountings_history_text
            account_number
            number_of_records
        }
    }
`;

export const GET_FILE_INFO_BY_ID = gql`
    query GetFileInfoById($fileid: uuid!) {
        file: file_files(where: { id: { _eq: $fileid } }) {
            id
            name
        }
    }
`;

export const GET_INPUTWORKFLOWS = gql`
    ${INPUTWORKFLOW_DETAILS}
    query GetInputWorkflows {
        inputworkflows: cred_inputworkflows(
            where: { state: { _nin: [9, 99] }, inputworkflowfiles: { state: { _nin: [99] } } }
            order_by: { inserted: desc }
        ) {
            ...inputworkflow_details
        }
    }
`;

export const GET_ACCOUNTINGS_HISTORY = gql`
    query GetAccountingsHistory($creditorId: uuid!) {
        accountings_history: cred_accountings_history(
            where: { creditorid: { _eq: $creditorId } }
            order_by: { inserted: desc }
            limit: 10
        ) {
            id
            creditorid
            accountnumber
            text
            system
        }
    }
`;

export const GET_NUMBER_OF_VISAS = gql`
    query GetNumberOfVisas {
        numberofvisas: cred_v_numberofvisas {
            realestateid
            numberofvisa
        }
    }
`;

/* MUTATIONS */

export const UPSERT_INVOICE = gql`
    mutation UpsertInvoice($invoice: cred_invoices_insert_input!) {
        upserted_invoice_data: insert_cred_invoices_one(
            object: $invoice
            on_conflict: {
                constraint: PK_invoices
                update_columns: [
                    amount
                    creditorid
                    creditorinfo
                    date
                    duedate
                    incidentid
                    insertuserid
                    invoiceinfo
                    invoicenumber
                    orderid
                    origin
                    paiddate
                    paymentaccountid
                    paymentinfo
                    payoutaccountid
                    realestateid
                    type
                    unitid
                    workflowinstance
                ]
            }
        ) {
            id
        }
    }
`;

export const UPSERT_ACCOUNTINGS = gql`
    mutation UpsertAccountings($accountingObjects: [cred_accountings_insert_input!]!) {
        upserted_accounting_data: insert_cred_accountings(
            objects: $accountingObjects
            on_conflict: {
                constraint: PK_accountings
                update_columns: [
                    amount
                    date
                    text
                    accountid
                    costcenterid
                    realestateid
                    extracostdate
                    quantity
                    unitofmeasure
                    vatcodeid
                    vatrate
                    netamount
                    vatamount
                ]
            }
        ) {
            returning {
                id
            }
        }
    }
`;
export const DELETE_ACCOUNTING = gql`
    mutation DeleteAccounting($accountingid: uuid!) {
        deleted_accouting: delete_cred_accountings(where: { id: { _eq: $accountingid } }) {
            returning {
                id
            }
        }
    }
`;

export const INSERT_WORKFLOW_USER = gql`
    mutation InsertWorkflowUser($workflowuser: cred_workflowusers_insert_input!) {
        insert_cred_workflowusers_one(object: $workflowuser) {
            id
            role
            signaturestamp
            user {
                id
                name1
                name2
            }
        }
    }
`;

export const UPDATE_USER_ID_FOR_WORKFLOW_USER = gql`
    mutation UpdateUserIdForWorkflowUser($invoiceid: uuid!, $role: Int!, $userid: uuid!) {
        update_cred_workflowusers(
            where: { invoiceid: { _eq: $invoiceid }, role: { _eq: $role } }
            _set: { userid: $userid }
        ) {
            affected_rows
            returning {
                user {
                    id
                    name1
                    name2
                }
            }
        }
    }
`;

export const UPSERT_INVOICE_COMMENT = gql`
    mutation UpsertInvoiceComment($comment: cred_invoicecomments_insert_input!) {
        insert_cred_invoicecomments_one(
            object: $comment
            on_conflict: { constraint: PK_invoicecomments, update_columns: [language, text] }
        ) {
            id
        }
    }
`;

export const UPDATE_INPUTWORKFLOW_FILE_STATE = gql`
    mutation UpdateInputWorkflowFileState($inputworkflowfileid: uuid!, $state: Int!) {
        updated_inputworkflowfiles: update_file_inputworkflowfiles(
            where: { id: { _eq: $inputworkflowfileid } }
            _set: { state: $state }
        ) {
            returning {
                id
                state
            }
        }
    }
`;

export const UPDATE_REALESTATE_ON_INPUTWORKFLOW_FILE = gql`
    mutation UpdateRealestateIdOnInputworkflowFile($inputWorkflowFileId: uuid!, $realestateId: uuid) {
        update_file_inputworkflowfiles(
            where: { id: { _eq: $inputWorkflowFileId } }
            _set: { realestateid: $realestateId }
        ) {
            affected_rows
        }
    }
`;

/* SUBSCRIPTIONS */

export const SUBSCRIBE_TO_INVOICE_WORKFLOWUSER_UPDATES = gql`
    ${USER_DETAIL}
    subscription SubscribeToInvoiceWorkflowUserUpdates($invoiceid: uuid!) {
        workflowusers: cred_workflowusers(where: { invoiceid: { _eq: $invoiceid } }, order_by: { role: asc }) {
            id
            role
            signaturestamp
            user {
                ...invoiceuser_details
            }
        }
    }
`;

export const SUBSCRIBE_TO_INVOICE_STATE_UPDATE = gql`
    subscription SubscribeToInvoiceStateUpdate($invoiceid: uuid!) {
        invoice: cred_invoices(where: { id: { _eq: $invoiceid } }) {
            id
            state
        }
    }
`;

export const SUBSCRIBE_TO_INPUTWORKFLOWS = gql`
    ${INPUTWORKFLOW_DETAILS}
    subscription SubscribeToInputWorkflows {
        inputworkflows: cred_inputworkflows(limit: 1, order_by: { updated: desc }) {
            ...inputworkflow_details
        }
    }
`;

export const SUBSCRIBE_TO_INPUTWORKFLOW_FILES = gql`
    ${INPUTWORKFLOW_FILE_DETAILS}
    subscription SubscribeToInputWorkflowFiles {
        inputworkflowfiles: file_inputworkflowfiles(
            limit: 1
            order_by: { updated: desc }
            where: { state: { _nin: [99] } }
        ) {
            ...inputworkflow_file_details
        }
    }
`;
